import { format, parseISO, addDays, addMinutes, isAfter, isBefore, startOfDay, getDay, differenceInDays } from 'date-fns';
/**
 * Convert HH:MM to minutes since midnight
 */
export function timeToMinutes(timeStr) {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
}
/**
 * Convert minutes since midnight to HH:MM
 * Handles overflow past midnight (e.g., 1440 -> 00:00)
 */
export function minutesToTime(minutes) {
    // Normalize to 0-1439 range (handles times >= 24:00)
    const normalizedMinutes = ((minutes % 1440) + 1440) % 1440;
    const hours = Math.floor(normalizedMinutes / 60);
    const mins = normalizedMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}
/**
 * Parse time range string like '08:00-12:00' to [startMinutes, endMinutes]
 */
export function parseTimeRange(rangeStr) {
    const [start, end] = rangeStr.split('-');
    return [timeToMinutes(start.trim()), timeToMinutes(end.trim())];
}
/**
 * Check if time falls within range (handles overnight ranges where end < start)
 */
export function isTimeInRange(timeMinutes, startMinutes, endMinutes) {
    // Handle overnight ranges (e.g., 13:00-04:00 where end < start)
    if (endMinutes <= startMinutes) {
        // Range spans midnight: time is in range if >= start OR < end
        return timeMinutes >= startMinutes || timeMinutes < endMinutes;
    }
    // Normal range
    return startMinutes <= timeMinutes && timeMinutes < endMinutes;
}
/**
 * Calculate available time slots given work hours and blocked times
 * Handles overnight work hours (e.g., 13:00 to 04:00) where end < start
 */
export function getAvailableSlots(workStart, workEnd, blockedSlots, minSlotSize = 15) {
    const workStartMin = timeToMinutes(workStart);
    const workEndMin = timeToMinutes(workEnd);
    const MINUTES_IN_DAY = 24 * 60; // 1440
    // Handle overnight work hours (e.g., 13:00 to 04:00)
    const isOvernight = workEndMin <= workStartMin;
    // Convert blocked slots to minutes
    const blockedRanges = blockedSlots.map(([start, end]) => [
        timeToMinutes(start),
        timeToMinutes(end),
    ]);
    // For overnight schedules, we have two work periods:
    // 1. From workStart to midnight (1440)
    // 2. From midnight (0) to workEnd
    // We calculate available slots in each period separately
    const available = [];
    if (isOvernight) {
        // For overnight schedules (e.g., 11 AM to 4 AM), we have two periods:
        // - Period 2: 00:00 to workEnd (early morning, e.g., 00:00-04:00)
        // - Period 1: workStart to midnight (evening, e.g., 11:00-24:00)
        // We generate Period 2 first so that tasks fill the early morning slots first,
        // then overflow to the evening slots. This makes the schedule appear in
        // chronological calendar order (00:00 first, then 11:00).
        // Period 2: midnight to workEnd (early morning hours)
        const period2Blocks = blockedRanges.filter(([s, e]) => s < workEndMin || e <= workEndMin);
        const period2Available = calculateSlotsInPeriod(0, workEndMin, period2Blocks, minSlotSize);
        available.push(...period2Available);
        // Period 1: workStart to midnight (evening hours)
        const period1Blocks = blockedRanges.filter(([s, e]) => s >= workStartMin || e > workStartMin);
        const period1Available = calculateSlotsInPeriod(workStartMin, MINUTES_IN_DAY, period1Blocks, minSlotSize);
        available.push(...period1Available);
    }
    else {
        // Normal daytime work hours
        const normalAvailable = calculateSlotsInPeriod(workStartMin, workEndMin, blockedRanges, minSlotSize);
        available.push(...normalAvailable);
    }
    return available;
}
/**
 * Helper function to calculate available slots within a single continuous period
 */
function calculateSlotsInPeriod(periodStart, periodEnd, blockedRanges, minSlotSize) {
    // Filter blocked ranges to only those that overlap with this period
    const relevantBlocks = blockedRanges
        .filter(([s, e]) => s < periodEnd && e > periodStart)
        .map(([s, e]) => [Math.max(s, periodStart), Math.min(e, periodEnd)]);
    // Sort blocked ranges by start time
    relevantBlocks.sort((a, b) => a[0] - b[0]);
    const available = [];
    let current = periodStart;
    for (const [blockedStart, blockedEnd] of relevantBlocks) {
        if (current < blockedStart) {
            // There's available time before this blocked slot
            if (blockedStart - current >= minSlotSize) {
                available.push([current, blockedStart]);
            }
        }
        current = Math.max(current, blockedEnd);
    }
    // Add remaining time after last blocked slot
    if (current < periodEnd) {
        if (periodEnd - current >= minSlotSize) {
            available.push([current, periodEnd]);
        }
    }
    return available;
}
/**
 * Calculate buffer time based on percentage
 */
export function calculateBufferTime(estimatedMinutes, bufferPercentage) {
    return Math.floor(estimatedMinutes * bufferPercentage / 100);
}
/**
 * Round minutes to nearest interval
 */
export function roundToInterval(minutes, interval = 5) {
    return Math.round(minutes / interval) * interval;
}
/**
 * "HH:MM" on a 12-hour clock: "09:30" → "9:30 AM", "13:05" → "1:05 PM".
 *
 * The scheduler writes a slot that ends at midnight as "24:00". Five private
 * copies of this lived in the app and three of them had no guard for it, so
 * the end of a late slot read "12:00 PM" — noon — on the schedule views and
 * in the task dialog. Anything unreadable comes back as given.
 */
export function formatClock12(time) {
    const match = /^(\d{1,2}):(\d{2})/.exec(time ?? '');
    if (!match)
        return time;
    const h = Number(match[1]) % 24;
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${h % 12 || 12}:${match[2]} ${ampm}`;
}
/**
 * Format duration in human-readable format
 */
export function formatDuration(minutes) {
    if (minutes < 60) {
        return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) {
        return `${hours}h`;
    }
    return `${hours}h ${mins}m`;
}
/**
 * Add minutes to a time string
 */
export function addMinutesToTime(timeStr, minutes) {
    let timeMinutes = timeToMinutes(timeStr);
    let newMinutes = timeMinutes + minutes;
    // Handle overflow past midnight
    if (newMinutes >= 24 * 60) {
        newMinutes = newMinutes % (24 * 60);
    }
    return minutesToTime(newMinutes);
}
/**
 * Calculate overlap in minutes between two time ranges
 */
export function calculateTimeOverlap(start1, end1, start2, end2) {
    const start1Min = timeToMinutes(start1);
    const end1Min = timeToMinutes(end1);
    const start2Min = timeToMinutes(start2);
    const end2Min = timeToMinutes(end2);
    const overlapStart = Math.max(start1Min, start2Min);
    const overlapEnd = Math.min(end1Min, end2Min);
    if (overlapStart >= overlapEnd) {
        return 0;
    }
    return overlapEnd - overlapStart;
}
/**
 * Get lowercase day name from a date
 */
export function getDayName(date) {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[date.getDay()];
}
/**
 * Get ISO date string (YYYY-MM-DD) from Date
 */
export function toISODateString(date) {
    return format(date, 'yyyy-MM-dd');
}
/**
 * Parse ISO date string to Date
 */
export function parseISODate(dateStr) {
    return parseISO(dateStr);
}
/**
 * Check if two dates are the same day
 */
export function isSameDay(date1, date2) {
    return toISODateString(date1) === toISODateString(date2);
}
/**
 * Check if date1 is before date2 (comparing days only)
 */
export function isBeforeDay(date1, date2) {
    return startOfDay(date1) < startOfDay(date2);
}
/**
 * Check if date1 is after date2 (comparing days only)
 */
export function isAfterDay(date1, date2) {
    return startOfDay(date1) > startOfDay(date2);
}
/**
 * Get start of today
 */
export function today() {
    return startOfDay(new Date());
}
/**
 * Days between two dates
 */
export function daysBetween(date1, date2) {
    return differenceInDays(date1, date2);
}
export { format, parseISO, addDays, addMinutes, isAfter, isBefore, startOfDay, getDay };
/**
 * What day it is, for the USER — the one definition, shared by everything.
 *
 * The app got this right by accident: it runs on the user's machine, so
 * `new Date()` was already their local time. The MCP server does not, and
 * `new Date()` there is UTC — which answered "what's on today" with tomorrow
 * for anyone west of Greenwich, for every hour after their 5pm.
 *
 * Rather than each caller deciding, both now ask this. Given a zone it
 * formats in that zone; given none it uses the host clock, which is correct
 * in a browser and is the honest fallback anywhere else.
 *
 * `en-CA` because it renders as YYYY-MM-DD, which is how this app keys every
 * date. Formatting rather than offset arithmetic means daylight saving is the
 * platform's problem — the difference between PST and PDT is exactly the kind
 * of thing a hardcoded offset gets wrong twice a year.
 */
export function todayIn(timezone, now = new Date()) {
    const format = timezone ? zonedFormat(timezone, 'date') : null;
    if (format) {
        try {
            return format.format(now);
        }
        catch {
            // An invalid Date: fall through to the same answer as with no zone.
        }
    }
    return toISODateString(now);
}
/**
 * One formatter per zone, made once. Building an `Intl.DateTimeFormat` costs
 * tens of microseconds — nothing for "what's today", but the badge engine,
 * streaks and analytics read thousands of completions in the user's zone, and
 * a fresh formatter per row took the badge engine to seconds on a long
 * history. A bad zone is remembered as `null`, so it falls back without
 * throwing again.
 */
const zonedFormats = new Map();
function zonedFormat(timezone, kind) {
    const key = `${kind}|${timezone}`;
    let format = zonedFormats.get(key);
    if (format === undefined) {
        try {
            format = new Intl.DateTimeFormat('en-CA', kind === 'date'
                ? { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }
                : {
                    timeZone: timezone,
                    year: 'numeric', month: '2-digit', day: '2-digit',
                    hour: '2-digit', minute: '2-digit', second: '2-digit',
                    hour12: false,
                });
        }
        catch {
            // Stored data can hold any string; a bad zone must not throw.
            format = null;
        }
        zonedFormats.set(key, format);
    }
    return format;
}
/**
 * "Now", as a Date whose LOCAL getters read as the user's wall clock.
 *
 * The companion to `todayIn`, and needed for the same reason: this server runs
 * in UTC, so `new Date()` here is not the user's clock. `todayIn` fixed the
 * DATE; the time of day was left, and the scheduler reads the time of day to
 * decide which slots have already passed.
 *
 * The trick is the parse. `new Date('2026-09-01T18:00:00')` — no trailing Z —
 * is parsed as LOCAL time, which on this server is UTC, so `getHours()`
 * returns 18. That is exactly what the scheduler wants: a Date that behaves
 * like the user's own browser clock, whatever machine it is running on.
 *
 * NOT a real instant. Its epoch value is wrong by the zone's offset, so never
 * use it for durations or to stamp a record — only for reading off the wall
 * clock the user is looking at.
 */
export function wallClockIn(timezone, now = new Date()) {
    const format = timezone ? zonedFormat(timezone, 'clock') : null;
    if (format) {
        try {
            const parts = format.formatToParts(now);
            const at = (type) => parts.find((p) => p.type === type)?.value ?? '00';
            // `hourCycle` quirk: some environments render midnight as "24".
            const hour = at('hour') === '24' ? '00' : at('hour');
            return new Date(`${at('year')}-${at('month')}-${at('day')}T${hour}:${at('minute')}:${at('second')}`);
        }
        catch {
            // Stored data can hold any string; a bad zone must not throw.
        }
    }
    return now;
}
/** Every IANA zone this platform knows, for a picker. */
export function knownTimezones() {
    try {
        const supported = Intl
            .supportedValuesOf?.('timeZone');
        if (supported?.length)
            return supported;
    }
    catch {
        // Older engines have no supportedValuesOf.
    }
    return [];
}
//# sourceMappingURL=date-utils.js.map